import React, { useState, useEffect } from "react";
import { Mic, Disc, Radio, BookOpen, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default content for when no articles exist in DB
const defaultCultureContent = [
  {
    id: "default-1",
    category: "Hip-Hop/RNB",
    title: "L'évolution du Rap Français",
    content: "Le rap français a connu une évolution remarquable depuis les années 80. De NTM à Booba, en passant par IAM et PNL, chaque génération a apporté sa pierre à l'édifice. Aujourd'hui, le rap français est l'un des plus dynamiques au monde, avec une nouvelle vague d'artistes qui repoussent les limites du genre.",
    author: "TIKA Team",
    isDefault: true
  },
  {
    id: "default-2",
    category: "Afrobeat",
    title: "L'Afrobeat Conquiert le Monde",
    content: "L'Afrobeat connaît un succès planétaire sans précédent. Des artistes comme Burna Boy, Wizkid et Davido remplissent des stades internationaux et collaborent avec les plus grandes stars mondiales. Ce phénomène témoigne de la richesse et de l'universalité de la musique africaine.",
    author: "TIKA Team",
    isDefault: true
  },
  {
    id: "default-3",
    category: "Dancehall/Caraïbes",
    title: "Le Dancehall : Des Rues de Kingston au Monde",
    content: "Né dans les sound systems jamaïcains des années 70, le dancehall a évolué pour devenir un phénomène mondial. De Shabba Ranks à Shenseea, le genre continue d'influencer la pop culture internationale et inspire des artistes du monde entier.",
    author: "TIKA Team",
    isDefault: true
  },
];

const getCategoryIcon = (category) => {
  switch(category) {
    case "Hip-Hop/RNB":
      return Mic;
    case "Afrobeat":
      return Disc;
    case "Dancehall/Caraïbes":
      return Radio;
    default:
      return BookOpen;
  }
};

const getCategoryColor = (category) => {
  switch(category) {
    case "Hip-Hop/RNB":
      return "from-pink-500 to-rose-500";
    case "Afrobeat":
      return "from-emerald-500 to-teal-500";
    case "Dancehall/Caraïbes":
      return "from-blue-500 to-cyan-500";
    default:
      return "from-primary to-primary/80";
  }
};

// Article Card Component with expand/collapse
const ArticleCard = ({ article }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = getCategoryIcon(article.category);
  const colorClass = getCategoryColor(article.category);
  
  // Strip HTML for preview text
  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };
  
  const plainText = stripHtml(article.content || "");
  const previewLength = 150;
  const needsTruncation = plainText.length > previewLength;
  const previewText = needsTruncation 
    ? plainText.substring(0, previewLength) + "..."
    : plainText;

  // Check if article has a custom image
  const hasMainImage = article.image && article.image.length > 0;

  return (
    <div
      data-testid={`culture-article-${article.id}`}
      className="culture-card glass-card rounded-lg p-5 border border-border hover:border-primary/40 transition-all"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        {/* Image or Icon */}
        {hasMainImage ? (
          <img 
            src={article.image} 
            alt={article.title}
            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center shadow-lg flex-shrink-0`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-block px-2 py-0.5 bg-gradient-to-r ${colorClass} text-white text-xs font-semibold rounded`}>
              {article.category}
            </span>
            {article.date && (
              <span className="text-xs text-muted-foreground">
                {article.date}
              </span>
            )}
          </div>
          <h3 className="text-base font-bold leading-tight">
            {article.title}
          </h3>
        </div>
      </div>
      
      {/* Content */}
      <div className="text-sm text-muted-foreground leading-relaxed">
        {isExpanded ? (
          <div 
            className="prose prose-sm prose-invert max-w-none
              [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-foreground
              [&_h2]:text-base [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-3 [&_h2]:text-foreground
              [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-2 [&_h3]:text-foreground
              [&_p]:mb-3 [&_p]:text-muted-foreground
              [&_img]:rounded-lg [&_img]:max-w-full [&_img]:my-4
              [&_strong]:text-foreground [&_strong]:font-semibold
              [&_em]:italic
              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3
              [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3
              [&_li]:mb-1
              [&_blockquote]:border-l-3 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-3
              [&_a]:text-primary [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        ) : (
          <p>{previewText}</p>
        )}
      </div>
      
      {/* Read more button */}
      {needsTruncation && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-3 text-primary hover:text-primary/80 p-0 h-auto font-medium"
          data-testid={`expand-article-${article.id}`}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4 mr-1" />
              Réduire
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-1" />
              Lire plus
            </>
          )}
        </Button>
      )}
      
      {/* Author */}
      {article.author && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/30">
          <Sparkles className="w-3 h-3 text-primary/60" />
          <span className="text-xs text-muted-foreground">
            Par {article.author}
          </span>
        </div>
      )}
    </div>
  );
};

export const CultureSection = ({ selectedCategory }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/culture?limit=50`);
      const dbArticles = response.data || [];
      
      // Combine DB articles with default content if no articles exist
      if (dbArticles.length === 0) {
        setArticles(defaultCultureContent);
      } else {
        setArticles(dbArticles);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des articles:", error);
      setArticles(defaultCultureContent);
    } finally {
      setLoading(false);
    }
  };

  // Filter articles based on selected category
  const filteredArticles = selectedCategory === "all"
    ? articles
    : articles.filter(article => 
        article.category === selectedCategory || 
        article.category === "Général"
      );

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Chargement des articles...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl sm:text-4xl text-street tracking-wide mb-3">
          Culture & Histoires
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto text-sm">
          Plongez dans l'univers de la musique urbaine
        </p>
      </div>

      {filteredArticles.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Aucun article dans cette catégorie pour le moment.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredArticles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      )}
    </div>
  );
};
